package hk.com.vr360_demo2.webapp.component.editor_rewrite

import cc.easyandroid.easycompose.mvi.store.Bootstrapper
import cc.easyandroid.easycompose.mvi.store.Executor
import cc.easyandroid.easycompose.mvi.store.Reducer
import cc.easyandroid.easycompose.mvi.store.StoreFactory
import cc.easyandroid.extensions_compose.mvi.coroutineBootstrapper
import cc.easyandroid.extensions_compose.mvi.coroutineExecutorFactory
import cc.easyandroid.extensions_compose.mvi.createReducer
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaLinkHotspot
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaSceneConfig
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaViewParameters
import hk.com.vr360_demo2.webapp.core.either.right
import hk.com.vr360_demo2.webapp.core.mvi.CommonEffect
import hk.com.vr360_demo2.webapp.core.mvi.EasyStoreViewModel
import hk.com.vr360_demo2.webapp.core.mvi.EffectEither
import hk.com.vr360_demo2.webapp.core.net.KtorPublicApi
import hk.com.vr360_demo2.webapp.core.net.common.PanoramaEditData
import hk.com.vr360_demo2.webapp.core.net.common.SaveSceneInfo
import hk.com.vr360_demo2.webapp.core.net.common.SceneInitViewParameters
import hk.com.vr360_demo2.webapp.core.net.common.SceneLinkHot
import hk.com.vr360_demo2.webapp.core.net.fold
import hk.com.vr360_demo2.webapp.core.ui_state.NetworkState
import hk.com.vr360_demo2.webapp.core.ui_state.UIComponent
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext

class EditorRewriteViewModel(
    private val id: String,
    private val token: String,
    private val publicApi: KtorPublicApi,
    restoredActiveSceneId: String?,
    storeFactory: StoreFactory,
) : EasyStoreViewModel<EditorRewriteIntent, EditorRewriteAction, EditorRewriteState, EditorRewriteMessage, EditorRewriteEffect>(
    storeFactory
) {

    override val initialState: EditorRewriteState = EditorRewriteState(activeSceneId = restoredActiveSceneId)

    override fun onCreateBootstrapper(): Bootstrapper<EditorRewriteAction> = coroutineBootstrapper {
        dispatch(EditorRewriteAction.InitialLoad)
    }

    override fun onCreatExecutor(): () -> Executor<EditorRewriteIntent, EditorRewriteAction, EditorRewriteState, EditorRewriteMessage, EffectEither<EditorRewriteEffect>> {
        return coroutineExecutorFactory {
            onIntent<EditorRewriteIntent.Refresh> {
                forward(EditorRewriteAction.InitialLoad)
            }

            onIntent<EditorRewriteIntent.SwitchScene> { intent ->
                if (state().scenes.any { scene -> scene.id == intent.sceneId }) {
                    dispatch(EditorRewriteMessage.SceneSwitched(intent.sceneId))
                }
            }

            onIntent<EditorRewriteIntent.UpdateScenes> { intent ->
                dispatch(EditorRewriteMessage.ScenesUpdated(intent.scenes))
            }

            onIntent<EditorRewriteIntent.ConfirmDeleteHotspotMock> { intent ->
                publish(
                    CommonEffect.ShowUIComponent(
                        UIComponent.AlertDialog(
                            title = "刪除熱點",
                            message = "確定要刪除此熱點嗎？",
                            action = {
                                accept(
                                    EditorRewriteIntent.SaveCurrentHotspotsMock(
                                        sceneId = intent.sceneId,
                                        hotspots = intent.hotspotsForSave,
                                        onSuccess = intent.onSuccess,
                                    ),
                                )
                            },
                        ),
                    ).right(),
                )
            }

            onIntent<EditorRewriteIntent.SaveCurrentHotspotsMock> { intent ->
                launch {
                    val mergedScenes = mergeScenes(intent.sceneId) { scene ->
                        scene.copy(linkHotspots = intent.hotspots)
                    }
                    val editData = buildEditDataFromScenes(intent.sceneId,mergedScenes)
                    publicApi.savePanoramaData(data = editData, token = token)
                        .fold(
                            onError = { error ->
                                publish(
                                    CommonEffect.ShowUIComponent(
                                        UIComponent.ToastSimple(error.message ?: "保存失敗"),
                                    ).right(),
                                )
                            },
                            onSuccess = {
                                updateScenes(mergedScenes)
                                publish(
                                    CommonEffect.ShowUIComponent(
                                        UIComponent.ToastSimple("設置成功", "success"),
                                    ).right(),
                                )
                                intent.onSuccess()
                            },
                        )
                }
            }

            onIntent<EditorRewriteIntent.SaveCurrentInitialViewMock> { intent ->
                launch {
                    val mergedScenes = mergeScenes(intent.sceneId) { scene ->
                        scene.copy(initialViewParameters = intent.initialViewParameters)
                    }
                    val editData = buildEditDataFromScenes(intent.sceneId,mergedScenes)
                    publicApi.savePanoramaData(data = editData, token = token)
                        .fold(
                            onError = { error ->
                                publish(
                                    CommonEffect.ShowUIComponent(
                                        UIComponent.ToastSimple(error.message ?: "保存失敗"),
                                    ).right(),
                                )
                            },
                            onSuccess = {
                                updateScenes(mergedScenes)
                                publish(
                                    CommonEffect.ShowUIComponent(
                                        UIComponent.ToastSimple("設置成功", "success"),
                                    ).right(),
                                )
                                intent.onSuccess()
                            },
                        )
                }
            }

            onAction<EditorRewriteAction.InitialLoad> {
                dispatch(EditorRewriteMessage.Loading)
                launch {
                    publicApi.loadVr360EditData(id = id, token = token).fold(
                        onError = { error ->
                            dispatch(EditorRewriteMessage.LoadFailure(error.message ?: "加載失敗"))
                        },
                        onSuccess = { response ->
                            val scenes = response.data?.modelInfoList?.map { modelInfo ->
                                PanoramaSceneConfig(
                                    id = modelInfo.id,
                                    url = modelInfo.tilesUrl.ifEmpty { modelInfo.url },
                                    name = modelInfo.name,
                                    cover = modelInfo.cover,
                                    initialViewParameters = PanoramaViewParameters(
                                        yaw = modelInfo.initialViewParameters?.yaw,
                                        pitch = modelInfo.initialViewParameters?.pitch,
                                        fov = modelInfo.initialViewParameters?.fov ?: 2.5,
                                    ),
                                    linkHotspots = modelInfo.linkHotspots.orEmpty().map { hotspot ->
                                        PanoramaLinkHotspot(
                                            yaw = hotspot.yaw,
                                            pitch = hotspot.pitch,
                                            target = hotspot.target,
                                            rotation = hotspot.rotation,
                                            imageName = hotspot.imageName,
                                        )
                                    },
                                )
                            }.orEmpty()
                            val uid = response.data.let { if (it?.version == 1) it.uid.toString() else null }
                            dispatch(EditorRewriteMessage.LoadSuccess(scenes, uid))
                        },
                    )
                }
            }
        }
    }

    override fun onCreateReducer(): Reducer<EditorRewriteState, EditorRewriteMessage> {
        return createReducer {
            onMessage<EditorRewriteMessage.Loading> {
                state.copy(
                    isLoading = true,
                    networkState = NetworkState.Good,
                )
            }
            onMessage<EditorRewriteMessage.LoadSuccess> { message ->
                val nextActiveSceneId = state.activeSceneId
                    ?.takeIf { currentId -> message.scenes.any { it.id == currentId } }
                    ?: message.scenes.firstOrNull()?.id
                state.copy(
                    isLoading = false,
                    scenes = message.scenes,
                    activeSceneId = nextActiveSceneId,
                    uid = message.uid,
                    networkState = NetworkState.Good,
                )
            }
            onMessage<EditorRewriteMessage.LoadFailure> { message ->
                state.copy(
                    isLoading = false,
                    scenes = emptyList(),
                    activeSceneId = null,
                    networkState = NetworkState.Failed(
                        message = message.message.takeIf { it.isNotBlank() } ?: "加載失敗",
                    ),
                )
            }
            onMessage<EditorRewriteMessage.SceneSwitched> { message ->
                state.copy(activeSceneId = message.sceneId)
            }
            onMessage<EditorRewriteMessage.ScenesUpdated> { message ->
                val nextActiveSceneId = state.activeSceneId
                    ?.takeIf { currentId -> message.scenes.any { it.id == currentId } }
                    ?: message.scenes.firstOrNull()?.id
                state.copy(
                    scenes = message.scenes,
                    activeSceneId = nextActiveSceneId,
                )
            }
        }
    }

    fun save(editData: PanoramaEditData) {
        launchBackground {
            withContext(Dispatchers.Default) {
                publicApi.savePanoramaData(data = editData, token = token)
            }
        }
    }

    private fun updateScenes(scenes: List<PanoramaSceneConfig>) {
        accept(EditorRewriteIntent.UpdateScenes(scenes))
    }

    private fun mergeScenes(
        sceneId: String,
        transform: (PanoramaSceneConfig) -> PanoramaSceneConfig,
    ): List<PanoramaSceneConfig> {
        return state.scenes.map { scene ->
            if (scene.id == sceneId) transform(scene) else scene
        }
    }

    private fun launchBackground(block: suspend () -> Unit) {
        coroutineScope.launch {
            block()
        }
    }


    private fun buildEditDataFromScenes(id:String ,scenes: List<PanoramaSceneConfig>): PanoramaEditData {
        val saveScenes = scenes.map { scene ->
            SaveSceneInfo(
                id = scene.id,
                initialViewParameters = SceneInitViewParameters(
                    scene.initialViewParameters?.yaw,
                    scene.initialViewParameters?.pitch,
                    scene.initialViewParameters?.fov,
                ),
                linkHotspots = scene.linkHotspots.orEmpty().map { hotspot ->
                    SceneLinkHot(
                        yaw = hotspot.yaw,
                        pitch = hotspot.pitch,
                        target = hotspot.target,
                        rotation = null,
                        imageName = hotspot.imageName,
                    )
                },
            )
        }
        //接口有变更，只传当前这一组
        return PanoramaEditData(saveScenes.filter { it.id ==  id})
    }
}
